import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkle, CheckCircle, Circle, Lightning, Compass, Heart, Brain, ArrowsClockwise, ArrowClockwise, Pulse, Fire } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const STAGE_ICON = { interrupt: Lightning, reframe: Brain, rehearse: ArrowsClockwise, restore: Heart };
const STAGE_COLOR = { interrupt: "text-amber-300", reframe: "text-sky-300", rehearse: "text-teal-300", restore: "text-rose-300" };
const FEELINGS = ["anxious", "angry", "sad", "overwhelmed", "numb", "stuck"];
const TABS = [
  { id: "recommended", label: "Recommended" },
  { id: "now", label: "Do This Now" },
  { id: "library", label: "Library" },
  { id: "plan", label: "Weekly Plan" },
  { id: "progress", label: "Progress" },
];

function PracticeCard({ p, onComplete }) {
  const [open, setOpen] = useState(false);
  const [rated, setRated] = useState(false);
  const Icon = STAGE_ICON[p.stage] || Lightning;
  return (
    <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid={`practice-${p.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-800 ${STAGE_COLOR[p.stage]}`}><Icon size={18} weight="duotone" /></span>
          <div>
            <h3 className="font-heading text-lg font-medium text-slate-100">{p.title}</h3>
            <div className="mt-1 flex flex-wrap gap-1.5">
              <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-xs capitalize text-slate-400">{p.stage}</span>
              <span className="rounded-full bg-slate-800 px-2.5 py-0.5 text-xs text-slate-400">{p.duration}</span>
              <span className="rounded-full bg-teal-500/10 px-2.5 py-0.5 text-xs text-teal-300">{p.pattern_name}</span>
            </div>
          </div>
        </div>
        {p.completed_count > 0 && <span className="shrink-0 font-mono text-xs text-slate-500">{p.completed_count}×</span>}
      </div>

      <p className="mt-4 text-sm leading-relaxed text-slate-300"><span className="text-teal-400">Why it helps: </span>{p.why}</p>
      <p className="mt-2 text-sm leading-relaxed text-slate-300"><span className="text-slate-400">Do this: </span>{p.micro}</p>

      <button onClick={() => setOpen((v) => !v)} className="mt-3 text-xs font-medium text-teal-400 hover:text-teal-300" data-testid={`practice-more-${p.id}`}>{open ? "Less" : "When & how →"}</button>
      {open && (
        <div className="mt-3 space-y-1.5 rounded-xl border border-slate-800 bg-slate-950/50 p-4 text-sm text-slate-400">
          <p><span className="text-slate-300">When it shows up: </span>{p.when}</p>
          <p><span className="text-slate-300">Success sign: </span>{p.success}</p>
          <p><span className="text-slate-300">Common obstacle: </span>{p.obstacle}</p>
          <p><span className="text-slate-300">Easier version: </span>{p.fallback}</p>
          <p className="italic"><span className="not-italic text-slate-300">Reflect after: </span>{p.reflect}</p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={() => onComplete(p.id)} disabled={p.done_today}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${p.done_today ? "border border-teal-500/40 text-teal-300" : "bg-teal-500 text-slate-950 hover:bg-teal-400"}`} data-testid={`practice-complete-${p.id}`}>
          {p.done_today ? <><CheckCircle size={16} weight="fill" /> Done today</> : <><Circle size={16} /> Mark practiced</>}
        </button>
        {p.done_today && !rated && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500" data-testid={`practice-rate-${p.id}`}>
            Helpful?
            {[1, 2, 3].map((n) => <button key={n} onClick={() => { api.post(`/practices/${p.id}/complete`, { usefulness: n * 2 }); setRated(true); toast.success("Thanks — this tunes your recommendations."); }} className="rounded-full border border-slate-700 px-2 py-0.5 hover:border-teal-500 hover:text-teal-300">{["Not really", "Somewhat", "A lot"][n - 1]}</button>)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function InnerPractices({ onNavigate }) {
  const [tab, setTab] = useState("recommended");
  const [data, setData] = useState(null);
  const [now, setNow] = useState(null);
  const [feeling, setFeeling] = useState(null);
  const [plan, setPlan] = useState(null);
  const [progress, setProgress] = useState(null);
  const [stageFilter, setStageFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/practices").then(({ data }) => setData(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "plan" && !plan) api.get("/practices/weekly-plan").then((r) => setPlan(r.data)); if (tab === "progress") api.get("/practices/progress").then((r) => setProgress(r.data)); }, [tab]);

  const complete = async (id) => {
    setData((d) => ({ ...d, weekly_done: d.weekly_done + 1,
      recommended: d.recommended.map((p) => p.id === id ? { ...p, done_today: true, completed_count: p.completed_count + 1 } : p),
      library: d.library.map((p) => p.id === id ? { ...p, done_today: true, completed_count: p.completed_count + 1 } : p) }));
    await api.post(`/practices/${id}/complete`);
    toast.success("Practiced. This is how insight becomes change.");
  };

  const doNow = async (f) => { setFeeling(f); const { data } = await api.post("/practices/do-now", { feeling: f }); setNow(data); };

  if (loading) return <div className="flex justify-center py-32"><div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;

  const lib = data.library.filter((p) => stageFilter === "all" || p.stage === stageFilter);

  return (
    <div data-testid="inner-practices">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">Inner Practices</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">Turn insight into change</h1>
      <p className="mt-2 max-w-xl text-sm text-slate-400">Small, situational practices for your actual patterns — the moments that matter, not a checklist.</p>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-800 pb-3">
        {TABS.map((t) => <button key={t.id} onClick={() => setTab(t.id)} className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all ${tab === t.id ? "bg-amber-500/15 text-amber-300" : "text-slate-400 hover:text-slate-200"}`} data-testid={`practices-tab-${t.id}`}>{t.label}</button>)}
      </div>

      {/* Recommended */}
      {tab === "recommended" && (
        <div className="mt-6">
          <div className="mb-4 flex items-center gap-2 text-sm text-slate-400"><Compass size={16} weight="duotone" className="text-teal-400" /> Tuned to: {data.recommended_for.join(" · ")} · <span className="text-teal-300">{data.weekly_done} practiced this week</span></div>
          <div className="space-y-4">{data.recommended.map((p) => <PracticeCard key={p.id} p={p} onComplete={complete} />)}</div>
        </div>
      )}

      {/* Do This Now */}
      {tab === "now" && (
        <div className="mt-6" data-testid="practices-donow">
          <p className="text-sm text-slate-300">What are you feeling right now?</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {FEELINGS.map((f) => <button key={f} onClick={() => doNow(f)} className={`rounded-full border px-4 py-2 text-sm capitalize transition-all ${feeling === f ? "border-amber-400 bg-amber-500/15 text-amber-300" : "border-slate-800 text-slate-300 hover:border-slate-600"}`} data-testid={`donow-feeling-${f}`}>{f}</button>)}
          </div>
          {now && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-6 space-y-4" data-testid="donow-result">
              <div className="rounded-2xl border border-rose-500/30 bg-rose-500/5 p-5"><p className="text-xs font-bold uppercase tracking-wider text-rose-300">Reset right now</p><p className="mt-2 text-sm text-slate-200">{now.reset_action}</p></div>
              {now.practice && <PracticeCard p={now.practice} onComplete={complete} />}
              <div className="rounded-2xl border border-indigo-500/30 bg-indigo-500/5 p-5"><p className="text-xs font-bold uppercase tracking-wider text-indigo-300">Or journal it</p><p className="mt-2 text-sm text-slate-300">{now.journal_prompt}</p><button onClick={() => onNavigate && onNavigate("journal")} className="mt-2 text-sm font-medium text-teal-400 hover:text-teal-300">Open journal →</button></div>
              <p className="text-xs text-slate-500">Feeling overwhelmed? Just do this: {now.fallback}</p>
            </motion.div>
          )}
        </div>
      )}

      {/* Library */}
      {tab === "library" && (
        <div className="mt-6" data-testid="practices-library">
          <div className="mb-4 flex flex-wrap gap-2">
            {["all", ...Object.keys(data.stages)].map((s) => <button key={s} onClick={() => setStageFilter(s)} className={`rounded-full border px-3 py-1.5 text-xs capitalize transition-all ${stageFilter === s ? "border-teal-400 bg-teal-500/15 text-teal-300" : "border-slate-800 text-slate-400 hover:border-slate-600"}`} data-testid={`library-filter-${s}`}>{s}{s !== "all" && <span className="ml-1 text-slate-500">· {data.stages[s]}</span>}</button>)}
          </div>
          <div className="space-y-4">{lib.map((p) => <PracticeCard key={p.id} p={p} onComplete={complete} />)}</div>
        </div>
      )}

      {/* Weekly Plan */}
      {tab === "plan" && (
        <div className="mt-6" data-testid="practices-plan">
          {!plan ? <div className="flex justify-center py-12"><div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div> : (
            <>
              <p className="text-sm text-slate-400">This week's focus: <span className="text-teal-300">{plan.focus_pattern}</span></p>
              <div className="mt-4 space-y-3">{plan.plan.map((s, i) => (
                <div key={i} className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
                  <div className="flex items-center gap-2"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500/15 font-mono text-xs text-amber-300">{i + 1}</span><h3 className="font-heading text-base font-medium text-slate-100">{s.step}</h3></div>
                  <p className="mt-2 text-sm text-slate-400">{s.text}</p>
                  {s.practice && <p className="mt-2 text-sm text-teal-300">→ {s.practice.title}: {s.practice.micro}</p>}
                </div>
              ))}</div>
            </>
          )}
        </div>
      )}

      {/* Progress */}
      {tab === "progress" && (
        <div className="mt-6" data-testid="practices-progress">
          {!progress ? <div className="flex justify-center py-12"><div className="h-7 w-7 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div> : (
            <>
              <div className="grid grid-cols-3 gap-3 text-center">
                {[["Total practiced", progress.total_completed, Pulse], ["Day streak", progress.streak, Fire], ["Active days", progress.active_days, CheckCircle]].map(([l, v, I]) => (
                  <div key={l} className="rounded-2xl border border-slate-800 bg-slate-900 p-5"><I size={18} weight="duotone" className="mx-auto text-teal-400" /><p className="mt-2 font-heading text-2xl text-slate-100">{v}</p><p className="text-xs text-slate-500">{l}</p></div>
                ))}
              </div>
              {progress.most_helpful?.length > 0 && (
                <div className="mt-5"><p className="text-xs font-bold uppercase tracking-wider text-slate-500">Your most-used practices</p>
                  <div className="mt-3 space-y-2">{progress.most_helpful.map((p) => <div key={p.id} className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-sm"><span className="text-slate-200">{p.title}</span><span className="font-mono text-xs text-slate-500">{p.count}× {p.avg_usefulness ? `· ${p.avg_usefulness}/6 helpful` : ""}</span></div>)}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
